/** @jsxImportSource @emotion/react */
import * as UI from "@goodgym/components";
import * as T from "@goodgym/graphql/types";
import SocialVisitPromoCard from "@goodgym/pages/SessionPage/components/SocialVisitPromoCard";
import React, { useEffect, useMemo, useRef, useState } from "react";
const CARD_WIDTH = 370;
const DEFAULT_CARD_GAP = 12;
const DEFAULT_SLIDE_INTERVAL = 6000;
const DEFAULT_TRANSITION_SEC = 0.7;

interface FeaturedCarouselProps {
  items: T.CoachReferralFragment[];
  headline?: string;
  slideInterval?: number;
  cardGap?: number;
  transitionSec?: number;
  visibleCount?: number;
}

const FeaturedCarousel: React.FC<FeaturedCarouselProps> = ({
  items,
  headline = "Social Visits",
  slideInterval = DEFAULT_SLIDE_INTERVAL,
  cardGap = DEFAULT_CARD_GAP,
  transitionSec = DEFAULT_TRANSITION_SEC,
  visibleCount = 1,
}) => {
  const [current, setCurrent] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const intervalRef = useRef<number | null>(null);
const theme = UI.useTheme();
const mdUp = UI.useMediaQuery(theme.breakpoints.up("md"));
const lgUp = UI.useMediaQuery(theme.breakpoints.up("lg"));

const itemsPerPage = useMemo(() => {
  if (lgUp) return 2; 
  if (mdUp) return 1; 
  return 1;
}, [mdUp, lgUp]);
  const maxIndex = useMemo(() => {
    return Math.max(0, Math.ceil(items.length / visibleCount) - 1);
  }, [items.length, visibleCount]);

const startAuto = React.useCallback(() => {
  if (intervalRef.current != null) return;
  if (items.length <= itemsPerPage) return;
  intervalRef.current = window.setInterval(() => {
    setCurrent((prev) => (prev + 1) % (maxIndex + 1));
  }, slideInterval);
}, [items.length, itemsPerPage, slideInterval, maxIndex]);

    const stopAuto = React.useCallback(() => {
      if (intervalRef.current != null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, []);

    useEffect(() => {
      if (current > maxIndex) setCurrent(0);
    }, [maxIndex, current]);

    useEffect(() => {
      startAuto();
      return stopAuto;
    }, [startAuto, stopAuto]);


    useEffect(() => {
      if (isHovered) stopAuto();
      else startAuto();
    }, [isHovered, startAuto, stopAuto]);

  const goTo = (idx: number) => setCurrent(idx);

 
  if (!items || items.length === 0) return null;

const containerPixelWidth =
  itemsPerPage * CARD_WIDTH + (itemsPerPage - 1) * cardGap;

  return (
    <UI.Box>
      {headline && (
        <UI.Typography
          variant="h5"
          component="h2"
          sx={{
            fontWeight: "bold",
            textAlign: "center",
            mb: 3,
            textTransform: "uppercase",
            letterSpacing: 1,
            color: "primary.main",
          }}
        >
          {headline}
        </UI.Typography>
      )}

      <UI.Box
        sx={{
          width: containerPixelWidth,
          maxWidth: "100%",
          mx: "auto",
          overflow: "hidden",
        }}
        onPointerEnter={() => setIsHovered(true)}
        onPointerLeave={() => setIsHovered(false)}
        onFocus={() => setIsHovered(true)}
        onBlur={() => setIsHovered(false)}
      >
        <UI.Box
          sx={{
            display: "flex",
            transition: `transform ${transitionSec}s cubic-bezier(.4,0,.2,1)`,
            transform: `translateX(-${
              current * (containerPixelWidth + cardGap)
            }px)`,
            gap: `${cardGap}px`,
          }}
        >
          {items.map((item, idx) => (
            <UI.Box
              key={`${item.__typename}-${item.id}`}
              sx={{
                minWidth: `${CARD_WIDTH}px`,
                maxWidth: `${CARD_WIDTH}px`,
                height: "400px",
                display: "flex",
                borderRadius: 3,
                boxShadow:
                  Math.floor(idx / visibleCount) === current
                    ? "0 4px 32px 0 #E1101833"
                    : "0 2px 8px #0002",
                background: "#fff",
                transition: "box-shadow 0.2s, border 0.2s",

                overflow: "hidden",
                flexShrink: 0,
              }}
            >
              <SocialVisitPromoCard
                coachReferral={item}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              />
            </UI.Box>
          ))}
        </UI.Box>

        <UI.Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 2,
            gap: 1,
          }}
        >
          {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
            <UI.IconButton
              key={idx}
              size="small"
              sx={{
                p: 0.5,
                color: current === idx ? "#E11018" : "#aaa",
                transition: "color 0.2s",
              }}
              onClick={() => goTo(idx)}
              aria-label={`Go to item ${idx + 1}`}
            >
              <span style={{ fontSize: 22, lineHeight: 1, display: "block" }}>
                &bull;
              </span>
            </UI.IconButton>
          ))}
        </UI.Box>
      </UI.Box>
    </UI.Box>
  );
};

export default FeaturedCarousel;
